import { Injectable, computed, inject, signal } from '@angular/core';
import { PromotionsApiService } from '../api/promotions-api.service';
import { CouponResponseDto } from '../api/promotions-api.types';
import {
  categoriaCuponADto,
  cuponADto,
  dtoACategoriaCupon,
  dtoACupon,
} from '../api/promotions.mapper';
import {
  CategoriaCupon,
  CategoriaCuponFormValue,
  CuponDescuento,
  CuponFormValue,
  cuponExpirado,
} from '../models/promociones.model';
import { CatalogoProductosService } from './catalogo-productos.service';

@Injectable({ providedIn: 'root' })
export class CuponesService {
  private api = inject(PromotionsApiService);
  private readonly catalogo = inject(CatalogoProductosService);

  readonly categorias = signal<CategoriaCupon[]>([]);
  readonly productos = this.catalogo.productos;

  private readonly cuponesDto = signal<CouponResponseDto[]>([]);
  readonly cupones = computed<CuponDescuento[]>(() =>
    this.cuponesDto().map((c) => dtoACupon(c, this.productos())),
  );

  readonly categoriasActivas = computed(() => this.categorias().filter((c) => c.activa));

  readonly cuponesVigentes = computed(() =>
    this.cupones().filter((c) => !cuponExpirado(c)),
  );

  readonly cuponesExpirados = computed(() =>
    this.cupones().filter((c) => cuponExpirado(c)),
  );

  constructor() {
    this.cargarCategorias();
    this.cargar();
  }

  cargar(): void {
    this.api.listarCupones().subscribe({
      next: (lista) => this.cuponesDto.set(lista),
      error: (error) => console.error('[Promociones] No se pudieron cargar los cupones', error),
    });
  }

  cargarCategorias(): void {
    this.api.listarCategoriasCupon().subscribe({
      next: (lista) => this.categorias.set(lista.map(dtoACategoriaCupon)),
      error: (error) =>
        console.error('[Promociones] No se pudieron cargar las categorias de cupon', error),
    });
  }

  obtenerPorId(id: string): CuponDescuento | undefined {
    return this.cupones().find((c) => c.id === id);
  }

  obtenerCategoria(id: string): CategoriaCupon | undefined {
    return this.categorias().find((c) => c.id === id);
  }

  crear(form: CuponFormValue): void {
    this.api.crearCupon(cuponADto(form)).subscribe({
      next: () => this.cargar(),
      error: (error) => console.error('[Promociones] No se pudo crear el cupon', error),
    });
  }

  actualizar(id: string, form: CuponFormValue): void {
    this.api.actualizarCupon(id, cuponADto(form)).subscribe({
      next: () => this.cargar(),
      error: (error) => console.error('[Promociones] No se pudo actualizar el cupon', error),
    });
  }

  eliminar(id: string): void {
    this.api.eliminarCupon(id).subscribe({
      next: () => this.cargar(),
      error: (error) => console.error('[Promociones] No se pudo eliminar el cupon', error),
    });
  }

  crearCategoria(form: CategoriaCuponFormValue): void {
    this.api.crearCategoriaCupon(categoriaCuponADto(form)).subscribe({
      next: () => this.cargarCategorias(),
      error: (error) => console.error('[Promociones] No se pudo crear la categoria', error),
    });
  }

  actualizarCategoria(id: string, form: CategoriaCuponFormValue): void {
    this.api.actualizarCategoriaCupon(id, categoriaCuponADto(form)).subscribe({
      next: () => this.cargarCategorias(),
      error: (error) => console.error('[Promociones] No se pudo actualizar la categoria', error),
    });
  }
}
